import { useEffect, useMemo, useState, type ReactNode } from 'react';
import uniq from 'lodash.uniq';

import { useGroupEntryViews } from '../../hooks/useGroupEntryViews';
import {
  createGroupEntryViewsStore,
  GroupEntryViewsStoreContext,
} from '../../state/groupEntryViews.store';

export function GroupEntryViewsProvider({
  children,
  entryIds,
  userId,
}: {
  readonly children: ReactNode;
  readonly entryIds: readonly string[];
  readonly userId: string;
}): ReactNode {
  const normalizedEntryIds = useMemo(() => uniq(entryIds).sort(), [entryIds]);

  return (
    <GroupEntryViewsBridge key={userId} entryIds={normalizedEntryIds} userId={userId}>
      {children}
    </GroupEntryViewsBridge>
  );
}

function GroupEntryViewsBridge({
  children,
  entryIds,
  userId,
}: {
  readonly children: ReactNode;
  readonly entryIds: readonly string[];
  readonly userId: string;
}) {
  const [store] = useState(createGroupEntryViewsStore);
  const { views, isLoading, isError } = useGroupEntryViews(entryIds, userId);
  const scopedViews = useMemo(
    () => new Map([...views].filter(([entryId]) => entryIds.includes(entryId))),
    [entryIds, views]
  );

  // A plain effect: a one-paint lag reads as loading, and useLayoutEffect caused re-dispatch loops.
  useEffect(() => {
    const state = store.getState();
    state.setViews(scopedViews);
    state.setStatus(isLoading, isError);
    state.pruneExpanded(new Set(entryIds));
  }, [entryIds, isError, isLoading, scopedViews, store]);

  return (
    <GroupEntryViewsStoreContext.Provider value={store}>
      {children}
    </GroupEntryViewsStoreContext.Provider>
  );
}
